/**
 * Where a link goes: a web page; a heading in the same note (`[text](#goals)`, `[[#Goals]]`);
 * a note by its title or path (`[[Plan]]`, `[[Plan#Goals|the plan]]`); or a note by a path
 * from the note it is in (`[text](../Plan.md#goals)`, or from the top with a leading /).
 */
export type EditorLink =
  | { kind: "url"; url: string }
  | { kind: "heading"; heading: string }
  | { kind: "wiki"; title: string; heading?: string }
  | { kind: "note"; path: string; heading?: string };

export interface BareUrlMatch {
  start: number;
  end: number;
  text: string;
}

export interface EditorLinkRange extends BareUrlMatch {
  className: "md-link" | "md-url" | "md-wikilink";
}

export interface TextRange {
  from: number;
  to: number;
}

const SAFE_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const BARE_URL_PATTERN = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
// A destination in < > may hold spaces: [Plan](<My Plan.md>).
const MARKDOWN_LINK_PATTERN = /!?\[[^\]\n]*\]\(\s*(?:<[^>\n]+>|[^\s)>]+)\s*\)/g;
const WIKI_LINK_PATTERN = /\[\[[^\]\n]+\]\]/g;
const SIMPLE_TRAILING_PUNCTUATION = new Set([".", ",", "!", "?", ";", ":"]);

export function parseEditorLink(text: string, baseUrl: string): EditorLink | null {
  const wikiMatch = text.match(/^\[\[([^\]]+)\]\]$/);
  if (wikiMatch) {
    // [[Plan#Goals|the plan]]: what follows | is only what the link says.
    const target = wikiMatch[1]!.split("|")[0]!;
    const hash = target.indexOf("#");
    const title = (hash === -1 ? target : target.slice(0, hash)).trim();
    const heading = hash === -1 ? "" : target.slice(hash + 1).trim();
    if (!title) return heading ? { kind: "heading", heading } : null;
    return heading ? { kind: "wiki", title, heading } : { kind: "wiki", title };
  }

  const markdownMatch = text.match(/^!?\[[^\]]*\]\(\s*(<[^>\n]+>|[^\s)>]+)\s*\)$/);
  if (markdownMatch) {
    const destination = markdownMatch[1]!.replace(/^<|>$/g, "").trim();
    return parseNoteLink(destination) ?? parseUrl(destination, baseUrl);
  }

  const rawDestination = text.trim();
  if (!/^(?:https?:\/\/|www\.)[^\s]+$/i.test(rawDestination)) return null;
  return parseUrl(rawDestination, baseUrl);
}

export function findBareUrls(text: string): BareUrlMatch[] {
  const matches: BareUrlMatch[] = [];
  for (const match of text.matchAll(BARE_URL_PATTERN)) {
    const raw = match[0];
    const start = match.index;
    if (start === undefined) continue;
    const value = trimTrailingPunctuation(raw);
    if (!value) continue;
    matches.push({ start, end: start + value.length, text: value });
  }
  return matches;
}

export function findEditorLinkRanges(text: string, excludedRanges: TextRange[] = []): EditorLinkRange[] {
  const ranges: EditorLinkRange[] = [];
  addPatternMatches(text, MARKDOWN_LINK_PATTERN, "md-link", ranges, excludedRanges);
  addPatternMatches(text, WIKI_LINK_PATTERN, "md-wikilink", ranges, excludedRanges);

  for (const match of findBareUrls(text)) {
    if (overlapsAny(match.start, match.end, excludedRanges) || overlapsAny(match.start, match.end, ranges)) continue;
    ranges.push({ ...match, className: "md-url" });
  }

  return ranges.sort((left, right) => left.start - right.start || left.end - right.end);
}

function addPatternMatches(
  text: string,
  pattern: RegExp,
  className: EditorLinkRange["className"],
  ranges: EditorLinkRange[],
  excludedRanges: TextRange[],
): void {
  for (const match of text.matchAll(pattern)) {
    if (match.index === undefined) continue;
    const start = match.index;
    const end = start + match[0].length;
    if (overlapsAny(start, end, excludedRanges)) continue;
    ranges.push({ start, end, text: match[0], className });
  }
}

function overlapsAny(start: number, end: number, ranges: Array<TextRange | BareUrlMatch>): boolean {
  return ranges.some((range) => {
    const rangeStart = "start" in range ? range.start : range.from;
    const rangeEnd = "end" in range ? range.end : range.to;
    return start < rangeEnd && end > rangeStart;
  });
}

/** A link within the vault: to a heading of this note, or to a Markdown note by its path. */
function parseNoteLink(destination: string): EditorLink | null {
  // Anything with a scheme (https:, mailto:) or a host (//host) is on the web.
  if (/^[a-z][a-z\d+.-]*:/i.test(destination) || destination.startsWith("//")) return null;
  const hash = destination.indexOf("#");
  const path = decodePart(hash === -1 ? destination : destination.slice(0, hash)).replace(/\?.*$/, "");
  const heading = hash === -1 ? "" : decodePart(destination.slice(hash + 1)).trim();
  if (!path) return heading ? { kind: "heading", heading } : null;
  if (!/\.md(?:\.age)?$/i.test(path)) return null;
  return heading ? { kind: "note", path, heading } : { kind: "note", path };
}

function decodePart(text: string): string {
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

/**
 * The vault path a note link names, from the note `from` it is in: a path starting with /
 * goes from the top of the vault, any other from `from`'s folder, through . and .. as
 * written. Null when it climbs out of the vault.
 */
export function notePathFromLink(from: string, link: string): string | null {
  const parts = link.startsWith("/") ? [] : from.split("/").slice(0, -1);
  for (const part of link.split("/")) {
    if (!part || part === ".") continue;
    if (part !== "..") parts.push(part);
    else if (!parts.pop()) return null;
  }
  return parts.join("/") || null;
}

function parseUrl(destination: string, baseUrl: string): EditorLink | null {
  const normalized = destination.replace(/^<|>$/g, "");
  const withProtocol = normalized.toLowerCase().startsWith("www.")
    ? `https://${normalized}`
    : normalized;
  try {
    const url = new URL(withProtocol, baseUrl);
    return SAFE_LINK_PROTOCOLS.has(url.protocol) ? { kind: "url", url: url.href } : null;
  } catch {
    return null;
  }
}

function trimTrailingPunctuation(value: string): string {
  let end = value.length;
  while (end > 0) {
    const last = value[end - 1];
    if (!last) break;
    if (SIMPLE_TRAILING_PUNCTUATION.has(last)) {
      end--;
      continue;
    }
    if (last === ")" && count(value.slice(0, end), ")") > count(value.slice(0, end), "(")) {
      end--;
      continue;
    }
    if (last === "]" && count(value.slice(0, end), "]") > count(value.slice(0, end), "[")) {
      end--;
      continue;
    }
    break;
  }
  return value.slice(0, end);
}

function count(value: string, character: string): number {
  return [...value].filter((candidate) => candidate === character).length;
}
